import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  CalendarClock,
  Check,
  ClipboardCheck,
  ClipboardPaste,
  ArrowRight,
  BookOpenText,
  Compass,
  Download,
  FileText,
  FolderPlus,
  LayoutGrid,
  List,
  type LucideIcon,
  Map as MapIcon,
  MoreHorizontal,
  Network,
  Share2,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Upload,
  ListTree,
} from "lucide-react";
import { type ReactNode, type Ref, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, api } from "../../api/client";
import type { Exercise, Zone } from "../../api/types";
import { AccountChip } from "../../auth/AccountMenu";
import { attentionSelector, designate, designateSoon } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { licenceScope } from "../../state/licenceScope";
import { GenerexMark, GenerexWordmark } from "../../components/GenerexMark";
import { Menu, MenuItem, MenuSeparator } from "../../components/Menu";
import { Motion, Presence } from "../../components/presence";
import { Badge, BetaBadge, Button, ButtonLink, IconButton, Kbd, KbdHint, SearchField, SectionLabel, Segmented } from "../../components/ui";
import { HelpButton } from "../../help/HelpButton";
import { FeedbackLauncher } from "../../feedback/FeedbackHost";
import { onboardingSeen, useHelp } from "../../help/helpStore";
import { k, lang, t } from "../../i18n/i18n";
import { useTrialExport } from "../../licence/trial";
import { LangSwitch } from "../../i18n/LangSwitch";
import { LicenceChip, LicenceDialog } from "../../licence/LicenceFooter";
import { ReadOnlyCallout } from "../../licence/ReadOnlyLicence";
import { TrialLicenceCallout } from "../../licence/TrialLicence";
import { GuideDownloadButton, GuideWelcomeCallout } from "../../help/guide";
import { useReadOnly } from "../../licence/readOnly";
import { levelLabel } from "../../missions";
import { useUi } from "../../state/ui";
import { keepsZone, recreateZoneAction, showZoneWait } from "../../map/zones/zoneAttention";
import { CampOutline } from "../../components/CampOutline";
import { FirstSteps, type StepTarget } from "./FirstSteps";
import { createExampleDossier, exampleDetail } from "./firstStepsState";
import { chiefNoticeText } from "../../views/received/chiefNotice";
import { DeletionContents } from "./DeletionContents";
import { DossierRemoval, removalMotion, useRemovalPhases } from "./dossierRemoval";
import { loadFailure } from "./loadFailure";
import { missingField } from "./missingData";
import { refreshAfterDossiersRemoved, useZoneList, ZONES_KEY } from "../../map/zones/useZones";
import { ZoneChipBadge } from "../../map/zones/ZoneChipBadge";
import { zoneChip, zoneReady } from "../../map/zones/zoneState";
import { BackupDialogHost } from "./BackupDialog";
import { MaintenanceDialogHost } from "./MaintenanceDialog";
import { PromotionDialogHost, PromotionSlot } from "./PromotionPanel";
import { ImportOpoDialog, isDossierFile, OPO_ACCEPTED, useOpoImport } from "./ImportOpoDialog";
import { IntroAnimation, whenIntroDone } from "./IntroAnimation";
import { useMountedAccount } from "../../auth/auth";
import { AccessBadges } from "../../sharing/AccessBadge";
import { accountsItem, type DossierFilter, isOwner, matchesFilter, useSharing } from "../../sharing/access";
import { ShareDialogHost } from "../../sharing/ReadOnlyBanner";
import {
  activityOf,
  enemyMeta,
  fold,
  forceAndMission,
  formatActivity,
  formatFullDate,
  importDossierFile,
  isComplete,
  chainRelation,
  isSubordinate,
  dossierZone,
  dossierZoneState,
  ordersState,
  type Part,
  partStates,
  searchText,
  relationText,
  stepCircle,
  stepText,
} from "./dossier";
import {
  declineAfterTerrain,
  GENERATE_TARGET,
  offerAfterImport,
  offerAfterTerrain,
  startAfterImport,
} from "../../views/declination/declination";
import { Waiting } from "../../components/Waiting";

/** Écran d'accueil (l'« Accueil » de l'iPad, à la charte web) : reprendre le dernier dossier, en créer un,
 *  en importer un, retrouver les autres — chacun reconnaissable à son camp, sa force, sa mission, son ennemi
 *  et l'état de ses ordres — et ouvrir directement ses Données initiales, sa carte ou son ordre. Montré à
 *  chaque ouverture et à chaque rechargement de l'application ; l'emblème de la barre latérale y ramène. */

type Target = "resume" | Part | "manoeuvre" | "declination";

const FADE_MS = 180;

export function WelcomeScreen() {
  const trialExport = useTrialExport();
  const queryClient = useQueryClient();
  // Pendant le fondu de sortie, l'accueil reste tel qu'il était (le dossier ouvert change derrière lui).
  const [leaving, setLeaving] = useState(false);
  const liveExerciseId = useUi((s) => s.exerciseId);
  const exerciseIdRef = useRef(liveExerciseId);
  if (!leaving) exerciseIdRef.current = liveExerciseId;
  const exerciseId = exerciseIdRef.current;
  const setExercise = useUi((s) => s.setExercise);
  const setView = useUi((s) => s.setView);
  const showObjects = useUi((s) => s.showObjects);
  const closeWelcome = useUi((s) => s.closeWelcome);
  const requestNameFocus = useUi((s) => s.requestNameFocus);
  const openOnboarding = useHelp((s) => s.openOnboarding);
  const intent = useHelp((s) => s.intent);
  const clearIntent = useHelp((s) => s.clearIntent);
  // Première visite du poste : la prise en main s'ouvre d'elle-même, une fois, après l'animation d'ouverture.
  useEffect(
    () =>
      whenIntroDone(() => {
        if (!onboardingSeen()) openOnboarding();
      }),
    [openOnboarding],
  );
  const notify = useUi((s) => s.notify);
  const layout = useUi((s) => s.homeLayout);
  const setLayout = useUi((s) => s.setHomeLayout);
  const sort = useUi((s) => s.homeSort);
  const setSort = useUi((s) => s.setHomeSort);

  const exercisesQuery = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  // Liste des zones relue tant que l'une se prépare : les pastilles des cartes de dossier avancent (un seul relevé).
  useZoneList(true, true);
  const exercises = exercisesQuery.data;
  const [licenceOpen, setLicenceOpen] = useState(false);
  const readOnlyPanel = useReadOnly((s) => s.panelOpen);
  const closeReadOnlyPanel = useReadOnly((s) => s.closePanel);

  // Affiché d'emblée (pas d'espace de travail entrevu à l'arrivée) ; au départ, fondu qui découvre l'espace de
  // travail, déjà chargé derrière.
  const [shown, setShown] = useState(true);
  const leave = useCallback(
    (then: () => void) => {
      setLeaving(true);
      then();
      setShown(false);
      window.setTimeout(closeWelcome, FADE_MS);
    },
    [closeWelcome],
  );

  const open = useCallback(
    (ex: Exercise, target: Target) =>
      leave(() => {
        if (ex.id !== exerciseId) setExercise(ex.id, ex.map_slug);
        // Carte d'un dossier dont la zone libre n'est pas prête : pas de dispositif à montrer, la vue Manœuvre
        // s'ouvre sur l'action attendue de la zone (carte de progression, « Réessayer »…, R12).
        const zone = target === "map" ? dossierZone(queryClient.getQueryData<Zone[]>(ZONES_KEY), ex.map_slug) : undefined;
        if (target === "mission" || (target === "resume" && ex.id !== exerciseId)) setView("mission");
        else if (target === "map" && zone && !zoneReady(zone)) showZoneWait(zone);
        else if (target === "map") showObjects("all");
        else if (target === "manoeuvre") setView("manoeuvre");
        else if (target === "briefing") setView("briefing");
        else if (target === "order") setView("order");
        else if (target === "direx") setView("timeline");
        else if (target === "aar") setView("evaluation");
        else if (target === "declination") setView("declination");
      }),
    [exerciseId, leave, queryClient, setExercise, setView, showObjects],
  );

  // Partie d'un dossier incomplet (R12) : le dossier s'ouvre sur ses Données initiales, le champ qui manque est
  // désigné (le nom du dossier à défaut) et un avis court explique pourquoi.
  const openIncomplete = (ex: Exercise) => {
    open(ex, "mission");
    notify({ text: t("Complétez d'abord les Données initiales"), tone: "info" });
    designateSoon(attentionSelector(missingField(ex)), "auto", 800, (found) => {
      if (!found) designate(attentionSelector("dossier-name"));
    });
  };

  const createDossier = () =>
    leave(() => {
      setExercise(null, null);
      setView("mission");
      requestNameFocus();
    });

  const importInput = useRef<HTMLInputElement>(null);
  // Échec d'un import ou de la création du dossier d'exemple : intitulé et détail, sous les dossiers.
  const [failure, setFailure] = useState<{ title: string; detail: string } | null>(null);
  const [importing, setImporting] = useState(false);
  const importDossier = async (file: File) => {
    setFailure(null);
    setImporting(true);
    try {
      const ex = await importDossierFile(file, queryClient);
      leave(() => {
        setExercise(ex.id, ex.map_slug);
        setView("mission");
        notify({
          text: t("Dossier importé : {name}", { name: ex.name }),
          tone: "ok",
          action: recreateZoneAction(keepsZone(ex.parameters, ex.notices)),
        });
      });
    } catch (err) {
      setFailure({ title: t("Import impossible."), detail: err instanceof Error ? err.message : "" });
    } finally {
      setImporting(false);
    }
  };

  // Dossier d'exemple (prise en main) : créé tout prêt, ouvert sur la carte et son dispositif.
  const [exampleBusy, setExampleBusy] = useState(false);
  const openExample = async () => {
    setFailure(null);
    setExampleBusy(true);
    try {
      const ex = await createExampleDossier(queryClient);
      leave(() => {
        setExercise(ex.id, ex.map_slug);
        showObjects("all");
        notify({
          text: t("Dossier d'exemple créé"),
          detail: exampleDetail(ex.example),
          tone: "ok",
        });
      });
    } catch (err) {
      setFailure({ title: t("Création de l'exemple impossible."), detail: err instanceof Error ? err.message : "" });
    } finally {
      setExampleBusy(false);
    }
  };

  // Suite donnée à la prise en main ouverte depuis l'accueil.
  useEffect(() => {
    if (!intent) return;
    clearIntent();
    if (intent === "create") createDossier();
    else void openExample();
  }, [intent]);

  // OPO importé (docs/20-import-opo.md) : dossier recréé, ouvert sur sa carte (sur l'attente de son terrain libre
  // tant qu'il se prépare : la liste des zones est relue d'abord, le terrain recréé n'y était pas), l'OPO importé
  // rangé dans ses ordres reçus.
  const opoOpen = useOpoImport((s) => s.openIn === "home");
  const opoFile = useOpoImport((s) => s.file);
  const openOpo = useOpoImport((s) => s.open);
  const closeOpo = useOpoImport((s) => s.close);
  const opoCreated = async (ex: Exercise) => {
    void queryClient.invalidateQueries({ queryKey: ["exercises"] });
    if (ex.map_slug?.startsWith("zone-")) await queryClient.invalidateQueries({ queryKey: ZONES_KEY }).catch(() => undefined);
    const decline = useOpoImport.getState().decline;
    closeOpo();
    // Brigade ou division : les ordres subordonnés se génèrent directement, l'action est désignée (docs/20 §13).
    const zone = dossierZone(queryClient.getQueryData<Zone[]>(ZONES_KEY), ex.map_slug);
    const ready = !zone || zoneReady(zone);
    // Déclinaison choisie à l'import : lancée aussitôt quand le terrain est prêt ; sinon le dossier s'ouvre sur sa
    // carte, et « Générer les ordres » reste offert dans la vue des ordres subordonnés.
    const offer = decline && offerAfterImport(ex.parameters.level, ex.map_slug, ready);
    const started = offer && (await startAfterImport(ex.id, lang()));
    // Terrain en préparation : l'avis « Terrain prêt » proposera la génération (intention retenue pour ce dossier).
    const later = decline && offerAfterTerrain(ex.parameters.level, ex.map_slug, ready);
    if (later) declineAfterTerrain(ex.id);
    open(ex, offer ? "declination" : "map");
    if (offer && !started) designateSoon(attentionSelector(GENERATE_TARGET), "auto", 900);
    notify({
      text: t("Dossier créé : {name}", { name: ex.name }),
      detail: offer
        ? started
          ? t("L'OPO importé est rangé dans ses ordres reçus ; les ordres des échelons inférieurs sont en cours de rédaction.")
          : t("L'OPO importé est rangé dans ses ordres reçus. Prochaine étape : générer les ordres des subordonnés.")
        : later
          ? t("L'OPO importé est rangé dans ses ordres reçus. Prochaine étape, dès que le terrain sera prêt : générer les ordres des subordonnés.")
          : t("L'OPO importé est rangé dans ses ordres reçus."),
      tone: "ok",
      action: recreateZoneAction(keepsZone(ex.parameters, ex.notices)),
    });
  };

  // OPO sans données Generex : la fenêtre se ferme sur la liste des dossiers, désignée (le fichier se range ensuite
  // dans les ordres reçus d'un dossier) ; sans dossier, sur la création d'un dossier.
  const chooseDossier = () => {
    closeOpo();
    designateSoon(attentionSelector("dossier-list"), "ring", 600, (found) => {
      if (!found) designate(attentionSelector("dossier-new"));
    });
  };

  const [deleting, setDeleting] = useState<Exercise | null>(null);
  // Purge de l'accueil (08/10/2026) : tous les dossiers de la licence, après confirmation.
  const [purging, setPurging] = useState(false);
  const purge = useMutation({
    mutationFn: api.purgeExercises,
    onSuccess: (done) => {
      for (const ex of exercises ?? []) licenceScope.forgetDossier(ex.id);
      setExercise(null, null);
      setPurging(false);
      queryClient.setQueryData<Exercise[]>(["exercises"], []);
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      void queryClient.invalidateQueries({ queryKey: ["opo-drafts"] });
      refreshAfterDossiersRemoved(queryClient);
      notify({ text: t("Tous les dossiers sont effacés"), detail: t("{n} dossier(s) supprimé(s) du serveur.", { n: done.exercises }), tone: "ok" });
    },
    onError: (error) => notify({ text: t("Purge impossible"), detail: (error as Error).message, tone: "error" }),
  });
  // Suppression (registre F38) : la carte s'efface dès la confirmation, la suppression se termine en arrière-plan ;
  // en échec, la carte revient et l'avis le dit, « Réessayer » désigné (aucun nouvel essai automatique).
  const [removal] = useState(
    () => new DossierRemoval({ remove: api.deleteExercise, onRemoved: () => undefined, onFailed: () => undefined }),
  );
  removal.setDeps({
    remove: (ex) => api.deleteExercise(ex),
    onRemoved: (ex) => {
      queryClient.setQueryData<Exercise[]>(["exercises"], (list) => list?.filter((e) => e.id !== ex.id));
      void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      refreshAfterDossiersRemoved(queryClient);
      if (ex.id === useUi.getState().exerciseId) setExercise(null, null);
    },
    onFailed: (ex, error) => {
      // Dossier modifié entre-temps sur un autre poste (412) : la liste est relue, la confirmation suivante porte sur
      // la version relue.
      const stale = error instanceof ApiError && error.status === 412;
      if (stale) void queryClient.invalidateQueries({ queryKey: ["exercises"] });
      notify({
        text: t("« {name} » n'a pas été supprimé", { name: ex.name }),
        detail: stale
          ? t("Ce dossier vient d'être modifié sur un autre poste : vérifiez-le, puis supprimez-le de nouveau.")
          : error instanceof Error && error.message
            ? error.message
            : t("La suppression a échoué : réessayez."),
        tone: "error",
        action: {
          label: t("Réessayer"),
          attention: true,
          run: () => setDeleting(queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === ex.id) ?? ex),
        },
      });
    },
  });
  const phases = useRemovalPhases(removal);

  const [query, setQuery] = useState("");
  // Comptes nominatifs (docs/17 §8.3) : tous, les miens, partagés avec moi, de l'unité ; absents en mode hérité.
  const [filter, setFilter] = useState<DossierFilter>("all");
  const me = useMountedAccount()?.id ?? null;
  const accounts = Boolean(exercises?.some(accountsItem));
  const searchRef = useRef<HTMLInputElement>(null);
  const q = fold(query.trim());
  const visible = useMemo(() => {
    // Dossiers subordonnés : ouverts depuis leur dossier d'origine, pas listés (08/10/2026).
    const list = exercises ?? [];
    const all = list.filter(
      (ex) =>
        phases.get(ex.id) !== "gone" &&
        !isSubordinate(ex, list) &&
        (!q || searchText(ex).includes(q)) &&
        matchesFilter(ex, filter, me),
    );
    if (sort === "name") return [...all].sort((a, b) => a.name.localeCompare(b.name, "fr", { numeric: true, sensitivity: "base" }));
    return [...all].sort((a, b) => activityOf(b).localeCompare(activityOf(a)));
  }, [exercises, q, sort, filter, me, phases]);
  const last = exercises?.find((ex) => ex.id === exerciseId) ?? null;
  // Dossiers subordonnés non comptés : ils ne sont pas listés (08/10/2026).
  const total = (exercises ?? []).filter((ex) => !isSubordinate(ex) && phases.get(ex.id) !== "gone").length;
  // Premiers pas : l'étape s'ouvre sur le dernier dossier ouvert, sinon sur le plus récent ; au-delà des Données
  // initiales, sur un dossier complet (à défaut, les Données initiales du dernier dossier, à compléter d'abord).
  const step = (target: StepTarget) => {
    const all = exercises ?? [];
    // La manœuvre se construit sur un camp ou une zone libre, jamais en CENTAC (la rotation suit les limites du camp).
    const pool =
      target === "mission" ? all : all.filter((ex) => isComplete(ex) && (target !== "manoeuvre" || ex.parameters.level !== "CENTAC"));
    const subject = last && pool.includes(last) ? last : pool[0];
    // Sans dossier complet : les Données initiales du dernier dossier, à compléter d'abord (pour la manœuvre,
    // un dossier hors CENTAC).
    const candidates = target === "manoeuvre" ? all.filter((ex) => ex.parameters.level !== "CENTAC") : all;
    const fallback = last && candidates.includes(last) ? last : candidates[0];
    if (target === "create" || all.length === 0) createDossier();
    else if (subject) open(subject, target);
    else if (fallback) open(fallback, "mission");
    else {
      // Seulement des dossiers CENTAC : la manœuvre ne s'y construit pas ; « Nouveau dossier » est désigné.
      notify({
        text: t("La manœuvre ne se construit pas en CENTAC"),
        detail: t("Créez un dossier de régiment, de brigade ou de division, sur un camp ou un terrain libre."),
        tone: "info",
      });
      designate(attentionSelector("dossier-new"));
    }
  };

  // « / » place le curseur dans la recherche, comme dans la plupart des applications web.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (e.key !== "/" || (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Action principale au clavier : « Reprendre » s'il y a un dossier récent, sinon « Nouveau dossier ».
  const primaryRef = useRef<HTMLButtonElement>(null);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current && exercises) {
      focused.current = true;
      primaryRef.current?.focus({ preventScroll: true });
    }
  }, [exercises]);

  // Recherche, tri et affichage des dossiers : en haut de l'écran, dans la barre fixe (demande d'Alexis du
  // 30/09/2026), dès qu'il y a des dossiers.
  const toolbar =
    exercises && total > 0 ? (
      <Motion origin="fade" className="col-span-2 row-start-2 flex min-w-0 items-center gap-2 lg:col-span-1 lg:col-start-2 lg:row-start-1">
        <SearchField
          className="w-[340px] min-w-0 max-lg:flex-1"
          inputRef={searchRef}
          value={query}
          onChange={setQuery}
          kbd="/"
          placeholder={t("Rechercher un dossier, un camp, une force…")}
          ariaLabel={t("Rechercher un dossier")}
        />
        <Segmented
          size="sm"
          fill={false}
          ariaLabel={t("Tri des dossiers")}
          value={sort}
          onChange={setSort}
          options={[
            { value: "recent", label: t("Récents") },
            { value: "name", label: t("Nom") },
          ]}
        />
        <Segmented
          size="sm"
          fill={false}
          role="tab"
          ariaLabel={t("Affichage des dossiers")}
          value={layout}
          onChange={setLayout}
          options={[
            { value: "grid", icon: LayoutGrid, label: <span className="sr-only">{t("Cartes")}</span>, title: t("Cartes") },
            { value: "list", icon: List, label: <span className="sr-only">{t("Liste")}</span>, title: t("Liste") },
          ]}
        />
      </Motion>
    ) : null;

  // Imports d'OPO en cours de vérification (docs/20 §12) : ils se reprennent depuis la fenêtre « Importer un OPO ».
  const { data: opoDrafts = [] } = useQuery({ queryKey: ["opo-drafts"], queryFn: api.opoDrafts, staleTime: 30_000 });
  const draftsCallout = (
    <Presence show={opoDrafts.length > 0}>
      {opoDrafts.length > 0 && (
        <Callout
          tone="info"
          className="mt-4"
          title={t("{n} import(s) d'OPO en cours de vérification", { n: opoDrafts.length })}
          action={
            <Button variant="link" onClick={() => openOpo("home")}>
              {t("Reprendre")}
            </Button>
          }
        >
          {opoDrafts.length > 1
            ? t("{name} et {n} autre(s)", { name: opoDrafts[0]!.name, n: opoDrafts.length - 1 })
            : opoDrafts[0]!.name}
        </Callout>
      )}
    </Presence>
  );
  const failureCallout = (
    <Presence show={Boolean(failure)}>
      {failure && (
        <Callout tone="danger" className="mt-4" title={failure.title} onClose={() => setFailure(null)}>
          {failure.detail || undefined}
        </Callout>
      )}
    </Presence>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("Accueil Generex")}
      data-welcome=""
      className={clsx(
        "absolute inset-0 z-[60] flex flex-col overflow-y-auto bg-canvas transition-opacity ease-out",
        shown ? "opacity-100" : "pointer-events-none opacity-0",
      )}
      style={{ transitionDuration: `${FADE_MS}ms` }}
    >
      {/* Animation d'ouverture : une fois par session, au premier affichage de l'accueil. */}
      <IntroAnimation />
      <header className="sticky top-0 z-10 grid min-h-14 shrink-0 grid-cols-[1fr_auto] items-center gap-x-6 gap-y-2 border-b border-line bg-surface/90 px-6 py-2.5 backdrop-blur lg:grid-cols-[1fr_auto_1fr]">
        {/* Logo à la place exacte de celui de la barre latérale (taille, 14 px du bord, centré sur 56 px de haut) :
            il ne bouge pas quand on passe de l'application à l'accueil et inversement. */}
        <div className="-my-2.5 -ml-2.5 flex h-14 items-center gap-2.5 self-start justify-self-start">
          <GenerexMark size={26} />
          <GenerexWordmark />
          <BetaBadge />
        </div>
        {toolbar}
        <div className="flex items-center gap-3 justify-self-end lg:col-start-3">
          {/* Licence du poste : sa fiche s'ouvre ici comme depuis la barre latérale (échéance en info-bulle). */}
          {/* Compte de la personne (comptes nominatifs, absent en mode hérité), puis licence du poste. */}
          <AccountChip />
          <LicenceChip onClick={() => setLicenceOpen(true)} />
          <LangSwitch />
          <div className="flex items-center gap-0.5">
            <Button variant="ghost" onClick={openOnboarding} title={t("Visite guidée en neuf étapes")}>
              <Compass size={14} /> {t("Prise en main")}
            </Button>
            {/* Retours des utilisateurs (registre F39) : bouton libellé, pastille des réponses à lire. */}
            <FeedbackLauncher place="home" />
            <HelpButton screen="accueil" size={16} />
          </div>
        </div>
      </header>

      <main
        className={clsx(
          "mx-auto w-full max-w-[1120px] flex-1 px-6 pt-10 pb-14 transition-transform ease-out",
          shown ? "translate-y-0" : "translate-y-1.5",
        )}
        style={{ transitionDuration: `${FADE_MS}ms` }}
      >
        {/* Préproduction seulement (registre F35) : versions et « Promouvoir… », au-dessus des dossiers. */}
        <PromotionSlot />
        <div className="max-w-[640px]">
          <h1 className="text-display leading-tight font-semibold tracking-[-0.01em] text-ink">{t("Dossiers d'exercice")}</h1>
          <p className="mt-2 text-title leading-relaxed text-ink-soft">
            {t("Chaque dossier réunit les données initiales, le dispositif sur la carte et les ordres d'opérations, de la compagnie à la division et pour la rotation CENTAC.")}
          </p>
          {/* Licence expirée : les dossiers restent consultables et téléchargeables, rien ne s'enregistre. */}
          <ReadOnlyCallout home className="mt-6" />
          {/* Licence d'essai (registre F44) : clé et validité, pour une licence d'essai seulement. */}
          <TrialLicenceCallout className="mt-6" />
          <GuideWelcomeCallout className="mt-6" />
        </div>

        {exercisesQuery.isError ? (
          <Callout
            tone="danger"
            className="mt-8"
            action={
              <Button variant="secondary" size="sm" onClick={() => void exercisesQuery.refetch()}>
                {t("Réessayer")}
              </Button>
            }
          >
            {loadFailure(exercisesQuery.error) === "server"
              ? t("Le serveur a rencontré une erreur en lisant les dossiers : réessayez dans un instant. Si l'erreur persiste, prévenez l'administrateur de Generex.")
              : t("Serveur injoignable : les dossiers n'ont pas pu être chargés. Vérifiez la connexion au réseau, puis réessayez.")}
          </Callout>
        ) : !exercises ? (
          <Skeleton />
        ) : total === 0 ? (
          <>
            <Onboarding
              primaryRef={primaryRef}
              onCreate={createDossier}
              onExample={() => void openExample()}
              exampleBusy={exampleBusy}
              onTour={openOnboarding}
              onImport={() => openOpo("home")}
              importing={importing}
            />
            {failureCallout}
          </>
        ) : (
          <>
            <Motion
              origin="fade"
              className={clsx("mt-8 grid gap-4", last ? "lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]" : "sm:grid-cols-3")}
            >
              {last && <ResumeCard ex={last} primaryRef={primaryRef} onOpen={open} onIncomplete={openIncomplete} />}
              <div className={last ? "grid auto-rows-fr gap-3 sm:grid-cols-2 lg:grid-cols-1" : "contents"}>
                {/* Un seul appel principal : « Reprendre » quand il y a un dossier récent, sinon « Nouveau dossier ». */}
                <ActionTile
                  buttonRef={last ? undefined : primaryRef}
                  icon={FolderPlus}
                  title={t("Nouveau dossier")}
                  text={t("Force commandée, mission, ennemi, camp ou terrain libre : le dispositif se place sur la carte.")}
                  onClick={createDossier}
                  emphasis={!last}
                  attention="dossier-new"
                />
                {/* Une seule entrée (08/10/2026) : fichier .generex, OPO de Generex ou d'état-major ; la fenêtre fait
                    choisir entre importer seul et importer puis décliner (aucun code de transmission). */}
                <ActionTile
                  icon={Upload}
                  title={importing ? <Waiting>{t("Import en cours…")}</Waiting> : t("Importer un OPO")}
                  text={t("OPO de Generex ou d'état-major (PDF, Word, LibreOffice, texte) ou dossier .generex : carte peuplée, et si vous le voulez, ordres des échelons inférieurs rédigés.")}
                  onClick={() => openOpo("home")}
                  disabled={importing}
                  attention="dossier-import"
                />
                <ActionTile
                  icon={Network}
                  title={t("Ordres de bataille")}
                  text={t("Vos ordres de bataille amis et ennemis (symboles OTAN APP-6), ou ceux fournis avec Generex : des forces pour vos dossiers.")}
                  onClick={() => leave(() => setView("orbats"))}
                />
              </div>
            </Motion>
            {/* Ordre d'état-major reçu en texte (courriel, messagerie) : la fenêtre d'import s'ouvre sans fichier, sur
                « Coller le texte de l'ordre ». */}
            <p className="mt-2 text-meta text-ink-muted">
              <Button variant="link" onClick={() => openOpo("home")} disabled={importing}>
                <ClipboardPaste size={12} /> {t("ou collez le texte d'un ordre")}
              </Button>
            </p>
            {/* Échec d'un import ou de l'exemple : sous les actions qui l'ont déclenché, sans défilement. */}
            {failureCallout}
            {draftsCallout}

            <FirstSteps exercises={exercises} onStep={step} secondary={!!last} />

            <Motion origin="fade" className="mt-10">
              <section aria-label={t("Tous les dossiers")} data-attention="dossier-list">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="flex items-center gap-2 text-title font-semibold text-ink">
                    {t("Tous les dossiers")}
                    <Badge>{q || filter !== "all" ? t("{shown} sur {total}", { shown: visible.length, total }) : total}</Badge>
                  </h2>
                  {!accounts && (exercises?.length ?? 0) > 0 && (
                    <Button variant="danger-ghost" size="sm" onClick={() => setPurging(true)}>
                      <Trash2 size={14} /> {t("Tout effacer…")}
                    </Button>
                  )}
                  {accounts && (
                    <Segmented
                      size="sm"
                      fill={false}
                      ariaLabel={t("Dossiers affichés")}
                      value={filter}
                      onChange={setFilter}
                      options={[
                        { value: "all", label: t("Tous") },
                        { value: "mine", label: t("Mes dossiers") },
                        { value: "shared", label: t("Partagés avec moi") },
                        { value: "unit", label: t("Unité") },
                      ]}
                    />
                  )}
                </div>

                {visible.length === 0 ? (
                  <div className="mt-4 rounded-xl border border-dashed border-line-strong px-5 py-8 text-center">
                    <p className="text-ui text-ink-soft">
                      {q ? t("Aucun dossier ne correspond à « {query} ».", { query: query.trim() }) : t("Aucun dossier dans ce filtre.")}
                    </p>
                    <Button
                      variant="link"
                      className="mt-2"
                      onClick={() => {
                        setQuery("");
                        setFilter("all");
                      }}
                    >
                      {q ? t("Effacer la recherche") : t("Voir tous les dossiers")}
                    </Button>
                  </div>
                ) : layout === "grid" ? (
                  <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {visible.map((ex) => (
                      <li key={ex.id} className={clsx("flex", removalMotion(phases.get(ex.id)))}>
                        <DossierCard
                          ex={ex}
                          relation={relationText({ ...chainRelation(ex, exercises ?? []), children: 0 })}
                          subordinates={chainRelation(ex, exercises ?? []).children}
                          current={ex.id === exerciseId}
                          onOpen={open}
                          onIncomplete={openIncomplete}
                          onDelete={setDeleting}
                        />
                      </li>
                    ))}
                  </ul>
                ) : (
                  // Pas de rognage du cadre : le menu « … » d'une ligne doit pouvoir en déborder.
                  <ul className="mt-4 divide-y divide-line rounded-xl border border-line bg-surface">
                    {visible.map((ex, i) => (
                      <li key={ex.id} className={removalMotion(phases.get(ex.id))}>
                        <DossierRow
                          ex={ex}
                          relation={relationText({ ...chainRelation(ex, exercises ?? []), children: 0 })}
                          subordinates={chainRelation(ex, exercises ?? []).children}
                          current={ex.id === exerciseId}
                          onOpen={open}
                          onIncomplete={openIncomplete}
                          onDelete={setDeleting}
                          className={clsx(i === 0 && "rounded-t-xl", i === visible.length - 1 && "rounded-b-xl")}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </Motion>
          </>
        )}
      </main>

      <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-line px-6 py-4 text-meta text-ink-muted">
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>Generex Web · BELLICOM</span>
          <GuideDownloadButton variant="link" size="sm" label={t("Guide de prise en main (PDF)")} />
        </span>
        <span>
          {accounts
            ? t("Les dossiers sont enregistrés sur le serveur Generex. Chacun voit ses dossiers et ceux qu'on lui partage ; téléchargez-les au format .generex pour en garder une copie.")
            : t("Les dossiers sont enregistrés sur le serveur Generex et partagés entre les seuls postes de votre licence ; téléchargez-les au format .generex pour les garder sur ce poste.")}
        </span>
      </footer>
      {/* Fenêtre « Partager », dans le plan de l'accueil. */}
      <ShareDialogHost />

      <input
        ref={importInput}
        type="file"
        accept={`.generex,application/json,.json,${OPO_ACCEPTED}`}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file) return;
          if (isDossierFile(file)) void importDossier(file);
          else openOpo("home", file);
        }}
      />

      <Presence show={opoOpen}>
        {opoOpen && (
          <ImportOpoDialog
            initialFile={opoFile}
            onClose={closeOpo}
            onCreated={opoCreated}
            onDossierFile={(file) => {
              closeOpo();
              void importDossier(file);
            }}
            onChooseDossier={chooseDossier}
          />
        )}
      </Presence>
      {/* Fenêtre de la promotion (préproduction seulement), hors de la colonne qui glisse à l'apparition. */}
      <PromotionDialogHost />
      <MaintenanceDialogHost />
      <BackupDialogHost />

      <Presence show={purging}>
        {purging && (
          <ConfirmDialog
            title={t("Effacer tous les dossiers ?")}
            confirmLabel={t("Tout effacer définitivement")}
            busy={purge.isPending}
            onConfirm={() => purge.mutate()}
            onCancel={() => setPurging(false)}
          >
            <p>
              {t("Les {n} dossiers de la licence, dossiers subordonnés compris, et les imports d'OPO en cours sont supprimés du serveur : ordres, ordres reçus, DIREX, 3A. Rien ne peut être récupéré depuis Generex.", { n: exercises?.length ?? 0 })}
            </p>
          </ConfirmDialog>
        )}
      </Presence>
      <Presence show={Boolean(deleting)}>
        {deleting && (
          <ConfirmDialog
            title={t("Supprimer « {name} » ?", { name: deleting.name })}
            confirmLabel={t("Supprimer le dossier")}
            onConfirm={() => {
              // Retour immédiat (registre F38) : la fenêtre se ferme, la carte s'efface, le serveur termine.
              const target = exercises?.find((ex) => ex.id === deleting.id) ?? deleting;
              setDeleting(null);
              void removal.start(target);
            }}
            onCancel={() => setDeleting(null)}
            extra={
              <ButtonLink variant="ghost" href={api.exerciseDownloadUrl(deleting.id)} {...trialExport}>
                <Download size={14} /> {t("Télécharger d'abord")}
              </ButtonLink>
            }
          >
            {/* Liste exacte de ce qui est effacé, et ce que le fichier .generex en garde (audit M11). */}
            <DeletionContents exerciseId={deleting.id} />
          </ConfirmDialog>
        )}
      </Presence>

      {/* Fiche de la licence, dans le plan de l'accueil (au-dessus de la carte). */}
      {/* Ouverte aussi par le bandeau de la lecture seule ou son avis (licence expirée), dans le plan de l'accueil. */}
      <LicenceDialog
        open={licenceOpen || readOnlyPanel}
        onClose={() => {
          setLicenceOpen(false);
          closeReadOnlyPanel();
        }}
      />
    </div>
  );
}

/** Ligne d'information d'une carte de dossier. Seul le texte se tronque : `extra` (pastille d'état) reste
 *  entier à sa suite, même avec un nom long. */
function Meta({ icon, children, extra }: { icon: ReactNode; children: ReactNode; extra?: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-ui text-ink-soft">
      <span className="flex shrink-0 text-ink-muted">{icon}</span>
      <span className="min-w-0 truncate">{children}</span>
      {extra}
    </div>
  );
}

/** Accès aux dossiers subordonnés d'un dossier (ils ne sont pas listés dans « Tous les dossiers ») : ouvre sa vue
 *  « Ordres subordonnés », d'où chacun s'ouvre. */
function SubordinatesLink({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="pointer-events-auto relative flex min-w-0 items-center gap-2 rounded-md text-ui font-medium text-accent hover:underline"
    >
      <ListTree size={14} className="shrink-0" aria-hidden />
      <span className="truncate">
        {count === 1 ? t("1 dossier subordonné — ouvrir") : t("{n} dossiers subordonnés — ouvrir", { n: count })}
      </span>
    </button>
  );
}

/** Losange ennemi (symbole hostile) devant le nom de l'ennemi joué. */
function EnemyMark() {
  return <span className="block size-2.5 rotate-45 rounded-xs border-[1.5px] border-hostile" />;
}

/** Zone libre du dossier, d'après la liste des zones (relue par l'accueil tant que l'une se prépare). */
function useDossierZone(slug: string | null): Zone | undefined {
  const { data: zones } = useZoneList(Boolean(slug));
  return dossierZone(zones, slug);
}

/** Terrain du dossier pour son avancement : zone, camp (null) ou pas encore connu (`ZONE_UNKNOWN`). Si la lecture des
 *  zones échoue, la liste des cartes (camps et zones) tranche, pour qu'un dossier sur camp ne reste pas « en
 *  vérification ». */
function useDossierTerrain(slug: string | null) {
  const { data: zones, isError } = useZoneList(Boolean(slug));
  const { data: maps } = useQuery({ queryKey: ["maps"], queryFn: api.maps, enabled: Boolean(slug) && isError, staleTime: 30_000 });
  return dossierZoneState(slug, zones, maps);
}

/** Zone libre du dossier pas encore prête : pastille durable sur sa carte (docs/14 §2.7.5), la même que sur l'entrée
 *  « Manœuvre » (rang en file, pourcentage, échec, annulée). */
function ZoneStateBadge({ slug, onClick }: { slug: string | null; onClick?: () => void }) {
  const zone = useDossierZone(slug);
  const chip = zoneChip(zone);
  if (!chip) return null;
  if (!onClick) return <ZoneChipBadge chip={chip} className="shrink-0" />;
  // Sur une carte (ou une ligne) qui s'ouvre d'un clic, le contenu laisse passer le pointeur vers le bouton du
  // dossier ; la pastille le reprend pour montrer son infobulle, et un clic ouvre le dossier comme ailleurs.
  return (
    <span role="presentation" className="pointer-events-auto flex shrink-0 cursor-pointer" onClick={onClick}>
      <ZoneChipBadge chip={chip} />
    </span>
  );
}

function OrdersBadge({ ex }: { ex: Exercise }) {
  const state = ordersState(ex);
  if (state.tone === "muted") return <span className="text-ink-muted">{state.label}</span>;
  return <Badge tone={state.tone === "ok" ? "ok" : "neutral"}>{state.label}</Badge>;
}

/** Dernier dossier ouvert sur ce poste : « Reprendre » le rouvre là où on l'avait laissé. Seul appel principal de
 *  l'accueil quand il est affiché. */
function ResumeCard({
  ex,
  primaryRef,
  onOpen,
  onIncomplete,
}: {
  ex: Exercise;
  primaryRef: Ref<HTMLButtonElement>;
  onOpen: (ex: Exercise, target: Target) => void;
  onIncomplete: (ex: Exercise) => void;
}) {
  const enemy = enemyMeta(ex);
  return (
    <article className="flex flex-col rounded-xl border border-line bg-surface p-5 shadow-card">
      <SectionLabel as="h2" inset>
        {t("Reprendre")}
      </SectionLabel>
      <div className="mt-3 flex items-start gap-4">
        <CampOutline slug={ex.map_slug} size={84} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {ex.parameters.level && <Badge tone="accent">{levelLabel(ex.parameters.level, ex.level_label)}</Badge>}
          </div>
          <h3 className="mt-2 truncate text-heading leading-snug font-semibold text-ink">{ex.name}</h3>
          <p className="mt-0.5 truncate text-ui text-ink-soft">{forceAndMission(ex)}</p>
          <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
            <Meta icon={<MapIcon size={14} />} extra={<ZoneStateBadge slug={ex.map_slug} />}>
              {ex.map_name ?? t("Camp à choisir")}
            </Meta>
            {enemy && <Meta icon={<EnemyMark />}>{enemy}</Meta>}
            <Meta icon={<FileText size={14} />}>
              <OrdersBadge ex={ex} />
            </Meta>
            <Meta icon={<span className="block size-1.5 rounded-full bg-line-strong" />}>
              <span title={formatFullDate(activityOf(ex))}>{t("Modifié {when}", { when: formatActivity(activityOf(ex)) })}</span>
            </Meta>
          </div>
          {/* Dossier transmis : l'ordre de son chef a changé depuis la transmission (ordres transmis prévenus). */}
          {ex.chief_notice && (
            <p className="mt-2 flex items-start gap-2 text-ui leading-snug text-accent">
              <ListTree size={14} className="mt-0.5 shrink-0" aria-hidden />
              <span className="min-w-0">{chiefNoticeText(ex.chief_notice)}</span>
            </p>
          )}
        </div>
      </div>
      <Progress ex={ex} onOpen={onOpen} onIncomplete={onIncomplete} />
      <div className="mt-auto flex items-center gap-2 border-t border-line pt-4">
        <Button ref={primaryRef} size="lg" onClick={() => onOpen(ex, "resume")}>
          {t("Reprendre")} <ArrowRight size={14} />
        </Button>
        <KbdHint className="text-meta text-ink-muted max-sm:hidden">
          {t("ou")} <Kbd>{t("Entrée")}</Kbd>
        </KbdHint>
      </div>
    </article>
  );
}

/** Tuile d'action de l'accueil (nouveau dossier, réception, import, ordres de bataille) : carte posée, bordure plus
 *  marquée au survol. `emphasis` : l'action attendue quand aucun dossier n'est à reprendre (pastille noire). */
function ActionTile({
  icon: Icon,
  title,
  text,
  onClick,
  emphasis = false,
  disabled = false,
  buttonRef,
  attention,
}: {
  icon: LucideIcon;
  title: ReactNode;
  text: string;
  onClick: () => void;
  emphasis?: boolean;
  disabled?: boolean;
  buttonRef?: Ref<HTMLButtonElement>;
  /** Cible de `designate` (`data-attention`). */
  attention?: string;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      data-attention={attention}
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        "group flex h-full min-h-[96px] items-center gap-4 rounded-xl border bg-surface px-5 py-4 text-left shadow-card transition-colors",
        "hover:border-line-strong disabled:opacity-40",
        emphasis ? "border-dashed border-line-strong" : "border-line",
      )}
    >
      <span
        className={clsx(
          "inline-flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors",
          emphasis ? "bg-ink text-white" : "bg-canvas text-ink-soft group-hover:text-ink",
        )}
      >
        <Icon size={16} aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block text-title font-semibold text-ink">{title}</span>
        <span className="mt-0.5 block text-ui leading-snug text-ink-muted">{text}</span>
      </span>
    </button>
  );
}

const QUICK: { target: Part; label: string; icon: LucideIcon }[] = [
  { target: "mission", label: k("Données initiales"), icon: SlidersHorizontal },
  { target: "map", label: k("Carte"), icon: MapIcon },
  { target: "briefing", label: k("Mise en ambiance"), icon: BookOpenText },
  { target: "order", label: k("Ordre d'opérations"), icon: FileText },
  { target: "direx", label: k("DIREX"), icon: CalendarClock },
  { target: "aar", label: k("3A"), icon: ClipboardCheck },
];

/** Infobulle d'une partie : l'ouvrir et son état, ou ce qu'elle attend. */
function partTitle(ex: Exercise, part: Part, label: string, status: string): string {
  if (part !== "mission" && !isComplete(ex)) return t("{view} : complétez d'abord les Données initiales", { view: label });
  return `${t("Ouvrir : {view}", { view: label })} · ${status}`;
}

/** Accès direct aux parties d'un dossier ; carte et ordre attendent que le camp et le niveau soient choisis : le
 *  clic ouvre alors les Données initiales sur le champ qui manque (R12). Une pastille verte marque les documents
 *  déjà entamés (mise en ambiance, ordre, DIREX, 3A). */
function QuickLinks({
  ex,
  onOpen,
  onIncomplete,
}: {
  ex: Exercise;
  onOpen: (ex: Exercise, target: Target) => void;
  onIncomplete: (ex: Exercise) => void;
}) {
  const complete = isComplete(ex);
  const states = partStates(ex, useDossierTerrain(ex.map_slug));
  return (
    <div className="relative z-10 flex items-center gap-0.5">
      {QUICK.map((q) => {
        const state = states[q.target];
        const title = partTitle(ex, q.target, t(q.label), state.status);
        const marked = state.done && q.target !== "mission" && q.target !== "map";
        return (
          <IconButton
            key={q.target}
            size="sm"
            className="relative"
            blocked={q.target !== "mission" && !complete}
            onBlocked={() => onIncomplete(ex)}
            title={title}
            aria-label={title}
            onClick={() => onOpen(ex, q.target)}
          >
            <q.icon size={14} aria-hidden />
            {marked && <span className="absolute top-1 right-1 size-1.5 rounded-full bg-ok ring-1 ring-surface" />}
          </IconButton>
        );
      })}
    </div>
  );
}

/** Avancement du dernier dossier, dans le déroulé d'un exercice : six parties, de la saisie à la 3A, chacune
 *  avec son état ; la prochaine à faire est désignée, et chaque partie s'ouvre d'un clic (les Données initiales,
 *  sur le champ qui manque, tant que le dossier est incomplet). Seul indicateur d'avancement d'un dossier. */
export function Progress({
  ex,
  onOpen,
  onIncomplete,
}: {
  ex: Exercise;
  onOpen: (ex: Exercise, target: Target) => void;
  onIncomplete: (ex: Exercise) => void;
}) {
  const complete = isComplete(ex);
  const states = partStates(ex, useDossierTerrain(ex.map_slug));
  const done = QUICK.filter((q) => states[q.target].done).length;
  const next = QUICK.find((q) => !states[q.target].done)?.target;
  return (
    <section className="mt-5 mb-4 border-t border-line pt-4" aria-label={t("Avancement")}>
      <div className="flex items-baseline justify-between gap-3">
        <SectionLabel as="h3" inset>
          {t("Avancement")}
        </SectionLabel>
        <span className="text-meta text-ink-muted tabular-nums">{t("{done} sur {total}", { done, total: QUICK.length })}</span>
      </div>
      <ol className="mt-3 grid grid-cols-6">
        {QUICK.map((q, i) => {
          const state = states[q.target];
          const label = t(q.label);
          const current = q.target === next;
          const blocked = q.target !== "mission" && !complete;
          return (
            <li key={q.target} className="relative min-w-0">
              {i < QUICK.length - 1 && (
                <span
                  aria-hidden
                  className={clsx("absolute top-4 right-[calc(-50%+18px)] left-[calc(50%+18px)] h-px", state.done ? "bg-ok/60" : "bg-line-strong")}
                />
              )}
              <button
                type="button"
                aria-disabled={blocked || undefined}
                title={partTitle(ex, q.target, label, state.status)}
                onClick={() => (blocked ? onIncomplete(ex) : onOpen(ex, q.target))}
                className={clsx(
                  "flex w-full flex-col items-center gap-1 rounded-lg px-1 pt-1 pb-1.5 text-center transition-colors",
                  blocked ? "opacity-40" : "hover:bg-hover",
                )}
              >
                <span
                  className={clsx(
                    "inline-flex size-6 items-center justify-center rounded-full transition-colors",
                    stepCircle(state, current),
                  )}
                >
                  {state.done ? <Check size={12} strokeWidth={3} /> : <q.icon size={14} aria-hidden />}
                </span>
                <span className="mt-0.5 text-meta leading-tight font-medium text-balance text-ink">{label}</span>
                <span className={clsx("text-meta leading-tight", stepText(state, current))}>
                  <Waiting>{state.status}</Waiting>
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** Menu « … » d'un dossier (R9, menu partagé) : téléchargement et suppression. */
function DossierMenu({ ex, onDelete }: { ex: Exercise; onDelete: (ex: Exercise) => void }) {
  const trialExport = useTrialExport();
  const [open, setOpen] = useState(false);
  const openShare = useSharing((s) => s.open);
  const notify = useUi((s) => s.notify);
  const close = useCallback(() => setOpen(false), []);
  return (
    <Menu
      open={open}
      onClose={close}
      portal
      side="top"
      align="end"
      width={220}
      ariaLabel={t("Autres actions sur « {name} »", { name: ex.name })}
      wrapperClassName="relative z-10 inline-flex"
      trigger={
        <IconButton
          size="sm"
          onClick={() => setOpen((o) => !o)}
          title={t("Autres actions")}
          aria-label={t("Autres actions sur « {name} »", { name: ex.name })}
        >
          <MoreHorizontal size={14} />
        </IconButton>
      }
    >
      {accountsItem(ex) && <MenuItem icon={Share2} label={t("Partager…")} onSelect={() => openShare(ex)} />}
      {/* Licence d'essai (registre F44) : entrée grisée ; la raison s'affiche dessous (les avis sont cachés sous
          l'accueil) et au survol. */}
      <MenuItem
        icon={Download}
        label={t("Télécharger (.generex)")}
        href={api.exerciseDownloadUrl(ex.id)}
        blocked={trialExport.blocked}
        title={trialExport.title}
        hint={trialExport.title}
      />
      <MenuSeparator />
      {/* Suppression réservée au propriétaire (docs/17 §3.2) : pour les autres, l'entrée bloquée le dit. */}
      <MenuItem
        icon={Trash2}
        danger
        label={t("Supprimer le dossier…")}
        blocked={!isOwner(ex)}
        onBlocked={() =>
          notify({ text: t("Seul le propriétaire du dossier peut le supprimer, le partager ou en changer le propriétaire."), tone: "info" })
        }
        onSelect={() => onDelete(ex)}
      />
    </Menu>
  );
}

function DossierCard({
  ex,
  relation,
  subordinates,
  current,
  onOpen,
  onIncomplete,
  onDelete,
}: {
  ex: Exercise;
  /** Échelon supérieur qui lui a donné son ordre (docs/20 §13). */
  relation: string[];
  /** Dossiers subordonnés produits par ce dossier : non listés, ouverts d'ici (« Ordres subordonnés »). */
  subordinates: number;
  current: boolean;
  onOpen: (ex: Exercise, target: Target) => void;
  onIncomplete: (ex: Exercise) => void;
  onDelete: (ex: Exercise) => void;
}) {
  const enemy = enemyMeta(ex);
  return (
    <article
      className={clsx(
        "group relative flex w-full flex-col rounded-xl border bg-surface p-4 shadow-card transition-colors",
        "hover:border-line-strong focus-within:border-line-strong",
        current ? "border-accent/50" : "border-line",
      )}
    >
      {/* Tout le dossier s'ouvre d'un clic ; les actions du pied restent cliquables par-dessus. */}
      <button
        type="button"
        onClick={() => onOpen(ex, "mission")}
        aria-label={t("Ouvrir le dossier « {name} »", { name: ex.name })}
        className="absolute inset-0 rounded-xl"
      />
      <div className="pointer-events-none flex items-start gap-3">
        <CampOutline slug={ex.map_slug} size={44} />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-title font-semibold text-ink">{ex.name}</h3>
          <p className="truncate text-ui text-ink-soft">{forceAndMission(ex)}</p>
          <AccessBadges ex={ex} className="mt-1 flex flex-wrap items-center gap-1" />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {ex.parameters.level && <Badge tone="accent">{levelLabel(ex.parameters.level, ex.level_label)}</Badge>}
        </div>
      </div>
      <div className="pointer-events-none mt-3 space-y-1.5">
        <Meta icon={<MapIcon size={14} />} extra={<ZoneStateBadge slug={ex.map_slug} onClick={() => onOpen(ex, "mission")} />}>
          {ex.map_name ?? t("Camp à choisir")}
        </Meta>
        {enemy && <Meta icon={<EnemyMark />}>{enemy}</Meta>}
        <Meta icon={<FileText size={14} />}>
          <OrdersBadge ex={ex} />
        </Meta>
        {relation.length > 0 && <Meta icon={<ListTree size={14} />}>{relation.join(" · ")}</Meta>}
        {subordinates > 0 && <SubordinatesLink count={subordinates} onClick={() => onOpen(ex, "declination")} />}
      </div>
      {/* Activité sur sa propre ligne : à côté des raccourcis, « Dernier ouvert · il y a … » était tronqué. */}
      <p className="pointer-events-none mt-3 text-meta text-ink-muted" title={formatFullDate(activityOf(ex))}>
        {current && <span className="font-medium text-accent">{t("Dernier ouvert")} · </span>}
        {formatActivity(activityOf(ex))}
      </p>
      <div className="mt-2 flex items-center justify-end gap-2 border-t border-line pt-3">
        <div className="flex items-center gap-0.5">
          <QuickLinks ex={ex} onOpen={onOpen} onIncomplete={onIncomplete} />
          <DossierMenu ex={ex} onDelete={onDelete} />
        </div>
      </div>
    </article>
  );
}

function DossierRow({
  ex,
  relation,
  subordinates,
  current,
  onOpen,
  onIncomplete,
  onDelete,
  className,
}: {
  ex: Exercise;
  relation: string[];
  subordinates: number;
  current: boolean;
  onOpen: (ex: Exercise, target: Target) => void;
  onIncomplete: (ex: Exercise) => void;
  onDelete: (ex: Exercise) => void;
  className?: string;
}) {
  return (
    <div className={clsx("group relative flex min-h-14 items-center gap-3 px-4 py-2 transition-colors hover:bg-hover", className)}>
      <button
        type="button"
        onClick={() => onOpen(ex, "mission")}
        aria-label={t("Ouvrir le dossier « {name} »", { name: ex.name })}
        className={clsx("focus-inset absolute inset-0", className)}
      />
      <CampOutline slug={ex.map_slug} size={32} className="pointer-events-none" />
      <div className="pointer-events-none min-w-0 flex-[2]">
        <div className="flex items-center gap-2">
          <span className="truncate text-ui font-semibold text-ink">{ex.name}</span>
          {current && <span className="shrink-0 text-meta font-medium text-accent">{t("Dernier ouvert")}</span>}
          <AccessBadges ex={ex} className="hidden shrink-0 items-center gap-1 sm:inline-flex" />
        </div>
        <div className="truncate text-ui text-ink-soft">{forceAndMission(ex)}</div>
        {relation.length > 0 && (
          <div className="flex min-w-0 items-center gap-1.5 text-meta text-ink-muted">
            <ListTree size={13} className="shrink-0" aria-hidden />
            <span className="truncate">{relation.join(" · ")}</span>
          </div>
        )}
        {subordinates > 0 && <SubordinatesLink count={subordinates} onClick={() => onOpen(ex, "declination")} />}
      </div>
      <div className="pointer-events-none hidden min-w-0 flex-1 items-center gap-1.5 text-ui text-ink-soft md:flex">
        <span className="min-w-0 truncate">{ex.map_name ?? t("Camp à choisir")}</span>
        <ZoneStateBadge slug={ex.map_slug} onClick={() => onOpen(ex, "mission")} />
      </div>
      <div className="pointer-events-none hidden w-[88px] shrink-0 md:block">
        {ex.parameters.level && <Badge tone="accent">{levelLabel(ex.parameters.level, ex.level_label)}</Badge>}
      </div>
      <div className="pointer-events-none hidden w-[170px] shrink-0 text-ui lg:block">
        <OrdersBadge ex={ex} />
      </div>
      <div className="pointer-events-none hidden w-[150px] shrink-0 truncate text-right text-meta text-ink-muted sm:block" title={formatFullDate(activityOf(ex))}>
        {formatActivity(activityOf(ex))}
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        <QuickLinks ex={ex} onOpen={onOpen} onIncomplete={onIncomplete} />
        <DossierMenu ex={ex} onDelete={onDelete} />
      </div>
    </div>
  );
}

/** Premier accès, aucun dossier : le parcours en trois étapes et l'action attendue. */
function Onboarding({
  primaryRef,
  onCreate,
  onExample,
  exampleBusy,
  onTour,
  onImport,
  importing,
}: {
  primaryRef: Ref<HTMLButtonElement>;
  onCreate: () => void;
  onExample: () => void;
  exampleBusy: boolean;
  onTour: () => void;
  onImport: () => void;
  importing: boolean;
}) {
  const steps: { icon: LucideIcon; title: string; text: string }[] = [
    {
      icon: SlidersHorizontal,
      title: t("Données initiales"),
      text: t("Camp, niveau de l'ordre, force commandée, mission, ennemi et début de l'exercice."),
    },
    {
      icon: MapIcon,
      title: t("Dispositif sur la carte"),
      text: t("Unités placées selon la doctrine, fuseau et lignes, graphiques tactiques : tout se déplace à la main."),
    },
    {
      icon: FileText,
      title: t("Ordre d'opérations"),
      text: t("Rédigé d'après la carte, relu, validé et exporté en PDF, en français ou en anglais."),
    },
  ];
  return (
    <section className="mt-8 rounded-xl border border-line bg-surface p-6 shadow-card">
      <h2 className="text-title font-semibold text-ink">{t("Préparez votre premier exercice")}</h2>
      <ol className="mt-5 grid gap-4 md:grid-cols-3">
        {steps.map((step, i) => (
          <li key={step.title} className="rounded-lg bg-canvas p-4">
            <div className="flex items-center gap-2 text-ink">
              <span className="inline-flex size-6 items-center justify-center rounded-full bg-surface text-meta font-semibold text-ink-muted ring-1 ring-line-strong tabular-nums">
                {i + 1}
              </span>
              <step.icon size={16} className="text-ink-soft" aria-hidden />
              <span className="text-title font-semibold">{step.title}</span>
            </div>
            <p className="mt-2 text-ui leading-relaxed text-ink-soft">{step.text}</p>
          </li>
        ))}
      </ol>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Button ref={primaryRef} size="lg" onClick={onCreate}>
          <FolderPlus size={14} /> {t("Créer un dossier")}
        </Button>
        <Button variant="secondary" size="lg" onClick={onExample} disabled={exampleBusy}>
          <Sparkles size={14} /> {exampleBusy ? <Waiting>{t("Création de l'exemple…")}</Waiting> : t("Ouvrir un dossier d'exemple")}
        </Button>
        <Button variant="secondary" size="lg" onClick={onImport} disabled={importing}>
          <Upload size={14} /> {importing ? <Waiting>{t("Import en cours…")}</Waiting> : t("Importer un OPO")}
        </Button>
      </div>
      <Button variant="link" className="mt-4" onClick={onTour}>
        <Compass size={14} /> {t("Visite guidée de Generex Web, en neuf étapes")}
      </Button>
    </section>
  );
}

function Skeleton() {
  return (
    <div className="mt-8 animate-pulse space-y-4" aria-label={t("Chargement des dossiers")}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
        <div className="h-[208px] rounded-xl bg-line/60" />
        <div className="grid grid-rows-2 gap-4">
          <div className="rounded-xl bg-line/60" />
          <div className="rounded-xl bg-line/60" />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[168px] rounded-xl bg-line/60" />
        ))}
      </div>
    </div>
  );
}

